import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth, useLang, useCurrency, useMoney, API } from '../../App';

const STAGE_CODES = { open: 'PROSP', reviewing: 'DD', structuring: 'STRUCT', committed: 'ENG', closed: 'CLOS' };

const useUtcClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return now.toISOString().slice(11, 19);
};

const useMarketData = (userId) => {
  const [data, setData] = useState({ stats: null, pipeline: null });
  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    const load = async () => {
      const [s, p] = await Promise.all([
        axios.get(`${API}/stats/dashboard`).catch(() => null),
        axios.get(`${API}/deals/pipeline`).catch(() => null),
      ]);
      if (alive) setData({ stats: s?.data || null, pipeline: p?.data || null });
    };
    load();
    const id = setInterval(load, 60000);
    return () => { alive = false; clearInterval(id); };
  }, [userId]);
  return data;
};

const Tick = ({ k, v, tone }) => (
  <span className="gcfe-tick"><span className="k">{k}</span><span className={`v ${tone || ''}`}>{v}</span></span>
);

export default function TerminalStatusBar() {
  const { user } = useAuth();
  const { lang } = useLang();
  const { currency } = useCurrency();
  const money = useMoney();
  const clock = useUtcClock();
  const { stats, pipeline } = useMarketData(user?.user_id);
  const [wsOn, setWsOn] = useState(() => window.__gcfeWs === 'open');
  const fr = lang === 'fr';

  useEffect(() => {
    const h = (e) => setWsOn(e.detail === 'open');
    window.addEventListener('idfc:ws-status', h);
    return () => window.removeEventListener('idfc:ws-status', h);
  }, []);

  const ticks = useMemo(() => {
    if (!stats) return [];
    const coverage = stats.total_funding_required > 0
      ? Math.round(((stats.total_funding_required - stats.total_funding_gap) / stats.total_funding_required) * 100) : 0;
    const out = [
      { k: 'PIPELINE', v: money(stats.total_funding_required || 0), tone: 'amber' },
      { k: fr ? 'ÉCART' : 'GAP', v: money(stats.total_funding_gap || 0), tone: 'down' },
      { k: fr ? 'ENGAGÉ' : 'COMMITTED', v: `${money(stats.total_committed || 0)} ▲${coverage}%`, tone: 'up' },
      { k: fr ? 'DEALS ACTIFS' : 'ACTIVE DEALS', v: stats.active_deals ?? 0 },
      { k: fr ? 'PROJETS' : 'PROJECTS', v: stats.total_projects ?? 0 },
    ];
    if (pipeline?.stats?.by_stage) {
      out.push({ k: fr ? 'ÉTAPES' : 'STAGES', v: Object.entries(pipeline.stats.by_stage).map(([s, n]) => `${STAGE_CODES[s] || s} ${n}`).join(' · ') });
      Object.values(pipeline.stages || {}).flat().slice(0, 8).forEach((d) => {
        const title = fr && d.project_title_fr ? d.project_title_fr : d.project_title;
        out.push({ k: (title || '').slice(0, 34).toUpperCase(), v: `${fr ? 'ÉCART' : 'GAP'} ${money(d.project_gap || 0)}${d.project_readiness_grade ? ` · ${d.project_readiness_grade}` : ''}`, tone: d.project_readiness_grade === 'A' ? 'up' : '' });
      });
    }
    (stats.sector_breakdown || []).forEach((s) => out.push({ k: String(s._id).toUpperCase(), v: `${s.count} · ${money(s.funding || 0)}` }));
    return out;
  }, [stats, pipeline, money, fr]);

  const inst = (user?.institution_id || '').replace(/^inst_/, '').toUpperCase();
  const speed = `${Math.max(40, ticks.length * 6)}s`;

  return (
    <div className="gcfe-statusbar" data-testid="terminal-statusbar" role="status">
      <div className="gcfe-seg" data-testid="statusbar-connection">
        <span className={`gcfe-dot ${wsOn ? 'on' : 'off'}`} />
        <span>{wsOn ? 'LIVE' : 'OFFLINE'}</span>
      </div>
      <div className="gcfe-seg" data-testid="statusbar-clock">{clock} UTC</div>
      <div className="gcfe-seg hidden md:flex" data-testid="statusbar-security">GCFE · TLS · 2FA</div>
      <div className="gcfe-ticker" data-testid="statusbar-ticker" style={{ '--gcfe-ticker-speed': speed }}>
        {ticks.length > 0 && (
          <div className="gcfe-ticker-track">
            {[...ticks, ...ticks].map((t, i) => <Tick key={`${t.k}-${i}`} {...t} />)}
          </div>
        )}
      </div>
      <div className="gcfe-seg hidden lg:flex" data-testid="statusbar-user">
        <span className="text-white">{user?.name}</span>
        {inst && <span className="amber" style={{ color: '#F5C544' }}>▸ {inst}</span>}
      </div>
      <div className="gcfe-seg" data-testid="statusbar-currency">{currency}</div>
      <div className="gcfe-seg" data-testid="statusbar-lang">{lang.toUpperCase()}</div>
      <button
        type="button"
        className="gcfe-seg gcfe-seg-btn"
        onClick={() => window.dispatchEvent(new CustomEvent('gcfe:cmdk'))}
        data-testid="statusbar-cmdk-btn"
      >
        <span className="gcfe-key">⌘K</span>
        <span className="hidden sm:inline">{fr ? 'COMMANDES' : 'COMMANDS'}</span>
      </button>
    </div>
  );
}
